import { initializeApp, getApp, getApps } from 'firebase/app';
import { initializeFirestore, getFirestore, persistentLocalCache, persistentMultipleTabManager, doc, setDoc, getDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { isNativePlatform, registerForNativePush } from '../../../utils/nativePush';

// Same Firebase project as the iOS app and customer web app.
const firebaseConfig = {
  apiKey: "AIzaSyCxly5CbEUguNZ8OYf-_S0mvmSo7X0A2Xg",
  authDomain: "kfc-sk-6638f.firebaseapp.com",
  databaseURL: "https://kfc-sk-6638f-default-rtdb.firebaseio.com",
  projectId: "kfc-sk-6638f",
  storageBucket: "kfc-sk-6638f.firebasestorage.app",
  messagingSenderId: "153903088164",
  appId: "1:153903088164:web:9b89afbf90e622a95f4eb1",
  measurementId: "G-G00T1MGGVM"
};

const VAPID_KEY = 'BLdEUWsM2_g4QgiQnNnquxwtea_ELWnzBm_p7FUndxNnfeR5fuI0FMi3quJzdlJ1VD_RmJq7hn_CXG2TfrjldBg';

// Reuse the host customer app's initialized Firebase app (one platform, one app instance).
export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
// Safari blocks Firestore's fetch-streaming Listen channel with CORS
// errors — force long polling there instead of letting auto-detect fail
// noisily first. Must match FirebaseContext.jsx: whichever module
// initializes first wins the config, so all sites need the same logic.
const isSafari = /^((?!chrome|chromium|android).)*safari/i.test(navigator.userAgent);
export const db = (() => {
  try {
    return initializeFirestore(app, {
      ...(isSafari
        ? { experimentalForceLongPolling: true }
        : { experimentalAutoDetectLongPolling: true }),
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    });
  } catch {
    return getFirestore(app);
  }
})();
export const auth = getAuth(app);
export const functions = getFunctions(app, 'us-central1');
export const functionsEast1 = getFunctions(app, 'us-east1');
export const storage = getStorage(app);

let messagingInstance = null;
export async function getMessagingInstance() {
  if (messagingInstance) return messagingInstance;
  try {
    if (await isSupported()) {
      messagingInstance = getMessaging(app);
      return messagingInstance;
    }
  } catch (e) {
    console.warn('FCM not supported:', e);
  }
  return null;
}

// Saves the web FCM token to users/{uid}/fcmTokens/{token} (same as customer web app).
export async function requestNotificationPermissionAndSaveToken(user) {
  if (isNativePlatform()) {
    return registerForNativePush(user, db);
  }
  const messaging = await getMessagingInstance();
  if (!messaging || !user) return null;
  // Safari requires a user gesture for the prompt — skip silently when none
  // has happened; granted users still get their token below.
  if (typeof Notification === 'undefined' || Notification.permission === 'denied') return null;
  if (Notification.permission === 'default' &&
      navigator.userActivation && !navigator.userActivation.hasBeenActive) return null;
  try {
    const permission = Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
    if (permission !== 'granted') return null;
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!token) return null;
    await setDoc(doc(db, 'users', user.uid, 'fcmTokens', token), {
      token,
      platform: 'web',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      userAgent: navigator.userAgent
    }, { merge: true });
    // Also mirror onto the user doc so iOS-style single-token lookups work.
    await setDoc(doc(db, 'users', user.uid), { fcmToken: token }, { merge: true });
    return token;
  } catch (e) {
    console.error('FCM token error:', e);
    return null;
  }
}

// Fetch a user's FCM token — checks the fcmToken field first (iOS writes this),
// then the fcmTokens subcollection (web writes this).
export async function getUserFCMToken(userId) {
  if (!userId) return null;
  try {
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (userSnap.exists() && userSnap.data().fcmToken) return userSnap.data().fcmToken;
    const tokensSnap = await getDocs(collection(db, 'users', userId, 'fcmTokens'));
    if (!tokensSnap.empty) return tokensSnap.docs[0].data().token || tokensSnap.docs[0].id;
  } catch (e) {
    console.warn('getUserFCMToken failed:', e);
  }
  return null;
}

// Sends a push via the deployed `sendNotification` callable — same contract as
// iOS PushNotificationManager: { token, message }.
export async function sendPushNotification(token, message) {
  if (!token) return;
  try {
    const fn = httpsCallable(functions, 'sendNotification');
    await fn({ token, message });
  } catch (e) {
    console.warn('sendNotification failed:', e);
  }
}

// Push to a user by id — resolved + sent server-side by the sendPushToUser
// callable (clients can't read other users' fcmTokens under the rules).
export async function sendPushToUser(userId, message) {
  if (!userId || !message) return;
  try {
    const fn = httpsCallable(functions, 'sendPushToUser');
    await fn({ userId, message });
  } catch (e) {
    console.warn('sendPushToUser failed:', e);
  }
}
